import React, { useState, useMemo } from "react";
import { useFetch } from "../Hooks/useFetch";
import { FetchBoundary } from "../common/FetchBoundary";
import { AccountsTable } from "./AccountsTable";
import { PurchaseValidationContext } from "./PurchaseValidationContext";
import { PurchaseValidationSearch } from "./PurchaseValidationSearch";
import { useSearchParams } from "../Hooks/useSearchParams";
import { PurchaseValidationDraw } from "./PurchaseValidationDraw";
import { ActionCards } from "./ActionCards";
import SubmissionModal from "../common/SubmissionModal";
import { AccountModal } from "./Modals/AccountModal";
import { withTheme } from "../common/ThemeProviderWrapper";
import { formatPharmacyGroupName } from "../utils/Helpers";

function PurchaseValidationPage({
  manufacturers,
  networks,
  organizationId,
  coveredEntity340bIds,
  coveredEntityNames,
  identifierMappingPath,
  purchaseValidationPath,
  downloadPath,
}) {
  const [searchParams] = useSearchParams();

  const [coveredEntity340bId, setCoveredEntity340bId] = useState(
    searchParams["id_340b"] ?? coveredEntity340bIds[0]
  );
  const [manufacturer, setManufacturer] = useState(searchParams["manufacturer"] ?? "all");
  const [network, setNetwork] = useState(searchParams["network"] ?? "all");
  const [searchInput, setSearchInput] = useState(searchParams["search"] ?? "");
  const [accountModal, setAccountModal] = useState(
    searchParams["account_id"] ? { accountId: searchParams["account_id"] } : undefined
  );
  const [submitClaimsModal, setSubmitClaimsModal] = useState(false);

  const accountsFetcher = useFetch(
    `/api/covered_entities/${coveredEntity340bId}/manufacturers/${manufacturer}/networks/${network}/accounts`
  );

  const { data: accountsResp } = accountsFetcher;

  const accounts = useMemo(() => {
    let newAccounts = [...(accountsResp?.accounts ?? [])];

    return newAccounts.filter((account) => {
      let visible = true;

      if (searchInput.length > 0) {
        visible = [
          account.group_name.replace("_", " "),
          formatPharmacyGroupName(account.group_name),
        ].some((name) => name?.toLowerCase().includes(searchInput.toLowerCase()));
      }

      return visible;
    });
  }, [accountsResp?.accounts, network, searchInput]);

  const allOtherAccounts = accounts.filter(
    (a) => !["at_risk", "suspended"].includes(a.display_status)
  );
  const atRiskAccounts = accounts.filter((a) => a.display_status === "at_risk");
  const suspendedAccounts = accounts.filter((a) => a.display_status === "suspended");
  const unassignedIdentifierCount = accountsResp?.unassigned_identifier_count ?? 0;

  const contextValues = {
    purchaseValidationPath,
    unassignedIdentifierCount,
    identifierMappingPath,
    downloadPath,
    coveredEntityNames,
    accounts,
    submitClaimsModal,
    setSubmitClaimsModal,
    manufacturers,
    manufacturer,
    setManufacturer,
    accountModal,
    setAccountModal,
    searchInput,
    setSearchInput,
    networks,
    network,
    setNetwork,
    coveredEntity340bIds,
    coveredEntity340bId,
    setCoveredEntity340bId,
    hasFetchedAccounts: accountsFetcher.hasFetched,
  };

  return (
    <PurchaseValidationContext.Provider value={contextValues}>
      <div className="flex">
        <div className="content__container flex-grow">
          <div className="page-details__container mb-0">
            <div className="page-details__container__info">
              <div className="page-details__title mb--32">Purchase Validation</div>
            </div>
          </div>

          <ActionCards />
          <PurchaseValidationSearch />

          <FetchBoundary {...accountsFetcher}>
            <AccountsTable
              statuses={["suspended"]}
              title={`Suspended Accounts`}
              accounts={suspendedAccounts}
            />
            <AccountsTable
              statuses={["at_risk"]}
              title={`At Risk Accounts`}
              accounts={atRiskAccounts}
            />

            <AccountsTable title={`All Other Accounts`} accounts={allOtherAccounts} statuses={[]} />
          </FetchBoundary>
        </div>

        <PurchaseValidationDraw />

        <AccountModal />

        {submitClaimsModal ? (
          <SubmissionModal
            organizationId={organizationId}
            handleCloseModal={() => setSubmitClaimsModal(false)}
          />
        ) : null}
      </div>
    </PurchaseValidationContext.Provider>
  );
}

export default withTheme(PurchaseValidationPage);
